<template>
	<p v-if="companiesData.length === 0" class="no-data">{{ t('no_data') }}</p>

	<DataTable :value="companiesData" tableStyle="min-width: 50rem" class="centered-table" v-else>
		<Column field="company" :header="t('dashboard_geo.landscape.companies')" bodyStyle="text-align: center" />
		<Column field="sentiment" bodyStyle="text-align: center">
			<template #header>
				<div class="flex align-items-center justify-content-center gap-1">
					<span>{{ t('dashboard_geo.landscape.sentiments') }}</span>
					<span @click.stop>
						<Tooltips position="top" :message="t('tooltips.dashboard_geo.sentiments')" />
					</span>
				</div>
			</template>
			<template #body="slotProps">
				<span :class="getClassSentiment(slotProps.data.sentiment)">{{ getSentimentIcon(slotProps.data.sentiment)
				}}</span>
			</template>
		</Column>
		<Column field="tonOfVoice" bodyStyle="text-align: center">
			<template #header>
				<div class="flex align-items-center justify-content-center gap-1">
					<span>{{ t('dashboard_geo.landscape.ton_of_voice') }}</span>
					<span @click.stop>
						<Tooltips position="top" :message="t('tooltips.dashboard_geo.tone_of_voice')" />
					</span>
				</div>
			</template>
		</Column>
		<Column field="shareOfVoice" style="width: 25%" bodyStyle="text-align: center">
			<template #header>
				<div class="flex align-items-center justify-content-center gap-1">
					<span>{{ t('dashboard_geo.landscape.share_of_voice') }}</span>
					<span @click.stop>
						<Tooltips position="top" :message="t('tooltips.dashboard_geo.share_of_voice')" />
					</span>
				</div>
			</template>
			<template #body="slotProps">
				<div class="bar-field">
					<div class="progress-bar">
						<div class="progress-fill" :style="{ width: slotProps.data.shareOfVoice + '%' }"></div>
					</div>
					<span class="progress-text">{{ slotProps.data.shareOfVoice }}%</span>
					<ComparatorValue style="margin-left: 1rem" :value="slotProps.data.shareOfVoice"
						:comparated="slotProps.data.lastShareOfVoice" :percent="slotProps.data.shareOfVoice" />
				</div>
			</template>
		</Column>
		<Column field="label" :header="t('dashboard_geo.landscape.product_services')" bodyStyle="text-align: center" />
		<Column field="keyBenefit" bodyStyle="text-align: center">
			<template #header>
				<div class="flex align-items-center justify-content-center gap-1">
					<span>{{ t('dashboard_geo.landscape.key_benefit') }}</span>
					<span @click.stop>
						<Tooltips position="top" :message="t('tooltips.dashboard_geo.key_benefit')" />
					</span>
				</div>
			</template>
		</Column>
	</DataTable>
</template>

<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ComparatorValue from '@/components/dashboard/comparatorValue.vue'
import Tooltips from '@/components/utilities/tooltips.vue'
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js'
import { useSubNavigationStore } from '@/store/global/subNavigation'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const subNavigationStore = useSubNavigationStore()
const { selectedAiTypeDashboard } = storeToRefs(subNavigationStore)

const geoStore = useDashboardGeoStore()
const { actors, scores, last_scores, sentiments, last_sentiments, analysis } = storeToRefs(geoStore)

const normalizeCompetitorName = (value) => {
	const raw = String(value || '').trim()
	if (!raw) {
		return ''
	}

	const cleaned = raw
		.replace(/^https?:\/\//i, '')
		.replace(/^www\./i, '')
		.split('/')[0]
		.split('?')[0]
		.split('#')[0]
		.split(':')[0]
		.trim()

	let baseName = cleaned
	if (cleaned && !cleaned.includes(' ') && cleaned.includes('.')) {
		const parts = cleaned
			.toLowerCase()
			.split('.')
			.map(part => part.trim())
			.filter(Boolean)
		const rootLikeParts = new Set(['co', 'com', 'net', 'org', 'gov', 'edu'])

		if (parts.length >= 3 && parts[parts.length - 1].length === 2 && rootLikeParts.has(parts[parts.length - 2])) {
			baseName = parts[parts.length - 3]
		}
		else if (parts.length >= 2) {
			baseName = parts[parts.length - 2]
		}
		else {
			baseName = parts[0] || cleaned
		}
	}

	return String(baseName || raw)
		.split(' ')
		.map(word => word.split('-').map(part => {
			if (!part) return part
			return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
		}).join('-'))
		.join(' ')
}

const getGlobalShareMaps = (scoreBucket = {}) => {
	const byActor = {}
	const byName = {}

	const byActorRaw = scoreBucket?.share_of_voice_by_actor || {}
	Object.entries(byActorRaw).forEach(([actorId, share]) => {
		byActor[Number(actorId)] = Number(share || 0)
	})

	const fullList = Array.isArray(scoreBucket?.share_of_voice_full_list) ? scoreBucket.share_of_voice_full_list : []
	fullList.forEach((row) => {
		const share = Number(row?.share || 0)
		const actorId = row?.actor_id
		if (actorId !== null && actorId !== undefined) {
			const id = Number(actorId)
			byActor[id] = Math.max(Number(byActor[id] || 0), share)
		}

		const normalized = normalizeCompetitorName(row?.name).toLowerCase()
		if (normalized) {
			byName[normalized] = Math.max(Number(byName[normalized] || 0), share)
		}
	})

	return { byActor, byName }
}

const getBestLabelMaps = (analysisBucket = {}) => {
	const byActor = {}
	const byName = {}

	const bestLabelRaw = analysisBucket?.best_label || {}
	Object.entries(bestLabelRaw).forEach(([actorId, label]) => {
		const text = String(label || '').trim()
		if (text) {
			byActor[Number(actorId)] = text
		}
	})

	const labelsDonut = Array.isArray(analysisBucket?.labels_donut) ? analysisBucket.labels_donut : []
	labelsDonut.forEach((labelRow) => {
		if (labelRow?.is_product_service !== true) {
			return
		}
		const labelName = String(labelRow?.name || '').trim()
		if (!labelName) {
			return
		}
		const segments = Array.isArray(labelRow?.segments) ? labelRow.segments : []
		segments.forEach((segment) => {
			if (segment?.is_other) {
				return
			}
			const normalized = normalizeCompetitorName(segment?.name).toLowerCase()
			if (!normalized) {
				return
			}
			const share = Number(segment?.share || 0)
			const mentionCount = Number(segment?.mention_count || 0)
			const current = byName[normalized]
			if (!current || share > current.share || (share === current.share && mentionCount > current.mentionCount)) {
				byName[normalized] = { label: labelName, share, mentionCount }
			}
		})
	})

	return { byActor, byName }
}

const companiesData = computed(() => {
	if (!actors.value.length) {
		return []
	}

	const aiType = selectedAiTypeDashboard.value

	const currentAiMaps = getGlobalShareMaps(scores.value?.[aiType] || {})
	const lastAiMaps = getGlobalShareMaps(last_scores.value?.[aiType] || {})
	const sentimentData = sentiments.value[aiType]?.sentiment_by_actor || {}
	const lastSentimentData = last_sentiments.value[aiType]?.sentiment_by_actor || {}
	const currentLabelMaps = getBestLabelMaps(analysis.value?.[aiType] || {})
	const globalLabelMaps = aiType === 'global' ? getBestLabelMaps(analysis.value?.global || {}) : { byActor: {}, byName: {} }
	const bestDrivers = analysis.value[aiType]?.best_driver || {}

	const result = actors.value.map(actor => {
		const actorSentiment = sentimentData[actor.id] || {}
		const lastActorSentiment = lastSentimentData[actor.id] || {}
		const normalizedActorName = normalizeCompetitorName(actor.name).toLowerCase()
		const currentShare = Math.max(
			Number(currentAiMaps.byActor[actor.id] || 0),
			Number(currentAiMaps.byName[normalizedActorName] || 0)
		)
		const previousShare = Math.max(
			Number(lastAiMaps.byActor[actor.id] || 0),
			Number(lastAiMaps.byName[normalizedActorName] || 0)
		)
		const bestLabel = (
			currentLabelMaps.byActor[actor.id]
			|| globalLabelMaps.byActor[actor.id]
			|| currentLabelMaps.byName[normalizedActorName]?.label
			|| globalLabelMaps.byName[normalizedActorName]?.label
			|| 'N/A'
		)
		return {
			id: actor.id,
			company: actor.name.charAt(0).toUpperCase() + actor.name.slice(1),
			sentiment: mapStringSentimentToIcon(actorSentiment.sentiment || 'Neutre'),
			lastSentiment: mapStringSentimentToIcon(lastActorSentiment.sentiment || 'Neutre'),

			tonOfVoice: actorSentiment.ton || 'Neutre',
			lastTonOfVoice: lastActorSentiment.ton || 'Neutre',

			label: bestLabel,
			keyBenefit: bestDrivers[actor.id] || 'N/A',

			shareOfVoice: Math.round(currentShare * 100),
			lastShareOfVoice: Math.round(previousShare * 100)
		}
	})

	return result
})

const mapStringSentimentToIcon = (sentimentString) => {
	const sentimentMap = {
		'Très positif': 'very-positive',
		'Positif': 'positive',
		'Neutre': 'neutral',
		'Négatif': 'negative',
		'Très négatif': 'very-negative'
	}
	return sentimentMap[sentimentString] || 'neutral'
}

const getClassSentiment = (sentiment) => {
	const classes = {
		'very-positive': 'icon icon-very-positive',
		'positive': 'icon icon-positive',
		'neutral': 'icon icon-neutral',
		'negative': 'icon icon-negative',
		'very-negative': 'icon icon-very-negative'
	}
	return classes[sentiment] || 'icon icon-neutral'
}

const getSentimentIcon = (sentiment) => {
	const icons = {
		'very-positive': '++',
		'positive': '+',
		'neutral': '=',
		'negative': '-',
		'very-negative': '--'
	}
	return icons[sentiment] || '='
};

</script>

<style lang="scss" scoped>
.centered-table :deep(.p-column-header-content) {
	justify-content: center !important;
	display: flex !important;
	text-align: center !important;
}

.bar-field {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: .5rem;

	.progress-bar {
		width: 60%;
		height: 10px;
		background-color: var(--gris-50);
		border-radius: 16px;
		overflow: hidden;

		.progress-fill {
			height: 100%;
			background: linear-gradient(90deg, var(--vert-300) 0%, var(--vert-500) 100%);
			border-radius: 12px;
			transition: width 0.3s ease;
		}
	}

	.progress-text {
		font-size: 12px;
		font-weight: 600;
		color: var(--gris-700);
		z-index: 1;
	}
}

.icon {
	padding: 0.3rem 0.5rem;
	border-radius: 4px;
	display: inline-block;
	color: var(--gris-800);
}

.icon-very-positive {
	background-color: var(--vert-500);
}

.icon-positive {
	background-color: var(--vert-200);
}

.icon-neutral {
	background-color: var(--jaune-500);
}

.icon-negative {
	background-color: var(--orange-400);
}

.icon-very-negative {
	background-color: var(--rouge-400);
}

.no-data {
	color: var(--gris-600);
	font-style: italic;
	text-align: center;
	padding: 1rem;
	font-size: 1.2rem;
}
</style>
